import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import BeritaOutlinePage from './BeritaOutlinePage'
import BeritaResultPage from './BeritaResultPage'
import { generateBeritaOptions, generateBeritaContent } from './beritaUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'

// Berita Agent States
const AGENT_STATES = {
  OUTLINE: 'outline',
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

function BeritaAgent({ query, onBack, llmService, options = {} }) {
  const [currentState, setCurrentState] = useState(AGENT_STATES.OUTLINE)
  const [beritaOptions, setBeritaOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)
  const [generatedContent, setGeneratedContent] = useState('')
  const [newsSources, setNewsSources] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [language, setLanguage] = useState(options.language || 'id') // 'id' for Indonesian, 'en' for English
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()

  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingOptions = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `berita_agent_${query}_${language}_${suffix}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedOptions = sessionStorage.getItem(getSessionKey('options'))
        const persistedContent = sessionStorage.getItem(getSessionKey('content'))
        const persistedSources = sessionStorage.getItem(getSessionKey('sources'))
        const persistedSelection = sessionStorage.getItem(getSessionKey('selection'))
        
        if (persistedState && persistedOptions) {
          const state = JSON.parse(persistedState)
          const optionsData = JSON.parse(persistedOptions)
          
          console.log('📋 Loading persisted berita agent state:', state.currentState)
          setCurrentState(state.currentState)
          setBeritaOptions(optionsData)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          if (persistedContent) {
            const contentData = JSON.parse(persistedContent)
            setGeneratedContent(contentData)
          }
          
          if (persistedSources) {
            const sourcesData = JSON.parse(persistedSources)
            setNewsSources(sourcesData)
          }
          
          if (persistedSelection) {
            const selectionData = JSON.parse(persistedSelection)
            setSelectedOption(selectionData)
          }
          
          // Don't generate options if we have persisted state
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('options'))
          sessionStorage.removeItem(getSessionKey('content'))
          sessionStorage.removeItem(getSessionKey('sources'))
          sessionStorage.removeItem(getSessionKey('selection'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query and language match
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip options generation
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        const oldKeys = [`berita_agent_${oldQuery}_id_`, `berita_agent_${oldQuery}_en_`]
        oldKeys.forEach(keyPrefix => {
          sessionStorage.removeItem(`${keyPrefix}state`)
          sessionStorage.removeItem(`${keyPrefix}options`)
          sessionStorage.removeItem(`${keyPrefix}content`)
          sessionStorage.removeItem(`${keyPrefix}sources`)
          sessionStorage.removeItem(`${keyPrefix}selection`)
        })
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
    }
    
    // Generate initial options only if needed
    if (query && !hasInitialized && !isGeneratingOptions.current && !hasEverGenerated.current) {
      generateInitialOptions()
    }
  }, [query, language]) // Depend on query and language

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && beritaOptions.length > 0) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('options'), JSON.stringify(beritaOptions))
        
        if (generatedContent) {
          sessionStorage.setItem(getSessionKey('content'), JSON.stringify(generatedContent))
        }
        
        if (newsSources.length > 0) {
          sessionStorage.setItem(getSessionKey('sources'), JSON.stringify(newsSources))
        }
        
        if (selectedOption) {
          sessionStorage.setItem(getSessionKey('selection'), JSON.stringify(selectedOption))
        }
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, beritaOptions, generatedContent, newsSources, selectedOption, hasInitialized, query, language])

  const generateInitialOptions = async () => {
    if (isGeneratingOptions.current || hasEverGenerated.current) {
      console.log('⚠️ Options generation already in progress or completed, skipping...')
      return
    }
    
    isGeneratingOptions.current = true
    
    try {
      setIsLoading(true)
      setError(null)
      console.log('🔄 Generating initial options for:', query, 'language:', language)
      
      const result = await generateBeritaOptions(query, llmService, language)
      setBeritaOptions(result.options)
      setNewsSources(result.sources)
      setHasInitialized(true)
      hasEverGenerated.current = true
      
      console.log('✅ Initial options generated successfully')
    } catch (error) {
      console.error('❌ Error generating initial options:', error)
      setError('Gagal membuat opsi berita')
      setHasInitialized(false)
      hasEverGenerated.current = false // Allow retry on error
    } finally {
      setIsLoading(false)
      isGeneratingOptions.current = false
    }
  }

  const handleLanguageChange = (newLanguage) => {
    // Clear old language session data
    try {
      const oldKeys = [`berita_agent_${query}_${language}_`]
      oldKeys.forEach(keyPrefix => {
        sessionStorage.removeItem(`${keyPrefix}state`)
        sessionStorage.removeItem(`${keyPrefix}options`)
        sessionStorage.removeItem(`${keyPrefix}content`)
        sessionStorage.removeItem(`${keyPrefix}sources`)
        sessionStorage.removeItem(`${keyPrefix}selection`)
      })
    } catch (error) {
      console.warn('⚠️ Failed to clear old language session data:', error)
    }
    
    setLanguage(newLanguage)
    setHasInitialized(false) // Reset to regenerate options in new language
    setBeritaOptions([])
    setNewsSources([])
    hasEverGenerated.current = false // Allow regeneration for new language
    isGeneratingOptions.current = false
  }

  const handleStartGeneration = async (option) => {
    // Check credits before starting generation
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.BERITA)
    if (!canProceed) {
      return
    }

    setCurrentState(AGENT_STATES.GENERATING)
    setSelectedOption(option)
    setProgress({ current: 0, total: 1 })
    setIsLoading(true)
    setError(null)
    
    try {
      setProgress({ current: 1, total: 1 })
      
      console.log(`🔄 Memproses berita: ${option.title}`)
      
      // Generate news content
      const content = await generateBeritaContent(option, query, llmService, newsSources, language)
      setGeneratedContent(content)
      
      console.log(`✅ Berita selesai diproses: ${option.title}`)
      
      setCurrentState(AGENT_STATES.COMPLETED)
      
      // Consume credits after successful completion
      try {
        const creditResult = await consumeCredits(AGENT_TYPES.BERITA)
        if (creditResult.success) {
          console.log('✅ Kredit berhasil dikonsumsi untuk berita agent')
        } else {
          console.warn('⚠️ Gagal mengkonsumsi kredit:', creditResult.error)
        }
      } catch (creditError) {
        console.error('❌ Error saat mengkonsumsi kredit:', creditError)
      }
      
      // Save to agent history after successful completion
      try {
        const saveResult = await AgentHistoryIntegration.saveBeritaResult({
          query: query,
          selectedOption: option,
          generatedContent: content,
          newsSources: newsSources,
          language: language,
          progress: { current: 1, total: 1 }
        })
        
        if (saveResult.success) {
          console.log('✅ Berita berhasil disimpan ke history:', saveResult.data)
        } else {
          console.warn('⚠️ Gagal menyimpan berita ke history:', saveResult.error)
        }
      } catch (saveError) {
        console.error('❌ Error saat menyimpan ke history:', saveError)
      }
    } catch (err) {
      console.error('❌ Error dalam handleStartGeneration:', err)
      setError(err.message)
      setCurrentState(AGENT_STATES.OUTLINE)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToSearch = () => {
    // Clear session storage when going back to search
    try {
      sessionStorage.removeItem(getSessionKey('state'))
      sessionStorage.removeItem(getSessionKey('options'))
      sessionStorage.removeItem(getSessionKey('content'))
      sessionStorage.removeItem(getSessionKey('sources'))
      sessionStorage.removeItem(getSessionKey('selection'))
    } catch (error) {
      console.warn('⚠️ Failed to clear session storage:', error)
    }
    
    // Reset state and go back to search
    setCurrentState(AGENT_STATES.OUTLINE)
    setBeritaOptions([])
    setSelectedOption(null)
    setGeneratedContent('')
    setNewsSources([])
    setProgress({ current: 0, total: 0 })
    setError(null)
    setHasInitialized(false)
    hasEverGenerated.current = false
    isGeneratingOptions.current = false
    onBack()
  }

  // Render based on current state
  switch (currentState) {
    case AGENT_STATES.OUTLINE:
      return (
        <BeritaOutlinePage
          query={query}
          beritaOptions={beritaOptions}
          onStartGeneration={handleStartGeneration}
          onBack={onBack}
          llmService={llmService}
          isLoading={isLoading}
          language={language}
          onLanguageChange={handleLanguageChange}
        />
      )
    
    case AGENT_STATES.GENERATING:
      case AGENT_STATES.COMPLETED:
      return (
        <BeritaResultPage
          query={query}
          selectedOption={selectedOption}
          generatedContent={generatedContent}
          newsSources={newsSources}
          progress={progress}
          isLoading={isLoading}
          error={error}
          isCompleted={currentState === AGENT_STATES.COMPLETED}
          language={language}
          onBack={() => setCurrentState(AGENT_STATES.OUTLINE)}
          onBackToSearch={handleBackToSearch}
        />
      )
    
    default:
      return (
        <>
          <CreditWarningModal 
            isOpen={warningModal.isOpen}
            onClose={closeWarning}
            creditsRequired={warningModal.creditsRequired}
            creditsRemaining={warningModal.creditsRemaining}
            packageName={warningModal.packageName}
            message={warningModal.message}
          />
          {null}
        </>
      )
  }
}

BeritaAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired,
  options: PropTypes.object
}

export default BeritaAgent